(() => {
  'use strict';
  if (window.alfuzonRefinementReady) return;
  window.alfuzonRefinementReady = true;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = matchMedia('(max-width: 749px)');
  const controllers = new Map();
  let alignFrame = 0;

  function alignCards() {
    cancelAnimationFrame(alignFrame);
    alignFrame = requestAnimationFrame(() => {
      document.querySelectorAll('.rf-product-row,.at-collection-grid,.as-shop-grid').forEach(grid => {
        if (!grid.getClientRects().length) return;
        const cards = [...grid.querySelectorAll('.rf-card')];
        cards.forEach(card => card.style.setProperty('--ar-title-height', '0px'));
        const rows = new Map();
        cards.forEach(card => {
          const title = card.querySelector('.rf-card-title');
          if (!title || !card.getClientRects().length) return;
          const row = Math.round(card.getBoundingClientRect().top / 5);
          if (!rows.has(row)) rows.set(row, []);
          rows.get(row).push({card, height: title.getBoundingClientRect().height});
        });
        rows.forEach(row => {
          const height = Math.ceil(Math.max(...row.map(item => item.height)));
          row.forEach(({card}) => card.style.setProperty('--ar-title-height', `${height}px`));
        });
      });
    });
  }

  function initSearch(form) {
    if (form.dataset.arReady) return;
    form.dataset.arReady = 'true';
    const input = form.querySelector('input[name="q"]');
    const panel = form.querySelector('.ar-search-popover');
    const results = form.querySelector('.ar-search-results');
    const feedback = form.querySelector('.ar-search-feedback');
    const popular = form.querySelector('[data-ar-popular]');
    const cache = new Map();
    let request, timer, sequence = 0, active = -1;
    const options = () => [...results.querySelectorAll('[role="option"]')];
    const abort = () => {
      clearTimeout(timer);
      if (request) request.abort();
      sequence++;
      input.setAttribute('aria-busy', 'false');
    };
    const open = () => {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };
    const close = () => {
      abort();
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      active = -1;
    };
    const render = html => {
      results.innerHTML = html;
      options().forEach((option, index) => {
        option.id = `${results.id}-option-${index}`;
        option.setAttribute('aria-selected', 'false');
      });
      active = -1;
      input.removeAttribute('aria-activedescendant');
      input.setAttribute('aria-busy', 'false');
      feedback.textContent = '';
      open();
    };
    const load = async () => {
      abort();
      const query = input.value.trim();
      if (!query) { render(popular.innerHTML); return; }
      if (cache.has(query)) { render(cache.get(query)); return; }
      const ticket = sequence;
      request = new AbortController();
      results.replaceChildren();
      input.removeAttribute('aria-activedescendant');
      active = -1;
      feedback.textContent = form.dataset.loading;
      input.setAttribute('aria-busy', 'true');
      open();
      try {
        const url = new URL(form.dataset.searchUrl, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('section_id', 'ar-predictive-search');
        const response = await fetch(url, {signal: request.signal, headers: {'X-Requested-With': 'XMLHttpRequest'}});
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (ticket !== sequence || input.value.trim() !== query) return;
        const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ar-search-content]');
        if (!content) throw new Error('Missing search content');
        // Prefer product-name matches when Shopify also returns description matches.
        const words = query.toLocaleLowerCase().split(/\s+/).filter(Boolean);
        const items = [...content.querySelectorAll('.ar-search-item')];
        const named = items.filter(item => words.every(word => item.querySelector('strong').textContent.toLocaleLowerCase().includes(word)));
        if (named.length) items.filter(item => !named.includes(item)).forEach(item => item.remove());
        if (cache.size > 40) cache.clear();
        cache.set(query, content.innerHTML);
        render(content.innerHTML);
        feedback.textContent = `${results.querySelectorAll('.ar-search-item').length} ${form.dataset.resultsLabel}`;
      } catch (error) {
        if (error.name === 'AbortError' || ticket !== sequence) return;
        input.setAttribute('aria-busy', 'false');
        feedback.textContent = form.dataset.error;
      }
    };
    input.addEventListener('focus', load);
    input.addEventListener('input', () => {
      abort();
      if (!input.value.trim()) { load(); return; }
      // Remove stale matches immediately while the next query is debounced.
      results.replaceChildren();
      input.removeAttribute('aria-activedescendant');
      active = -1;
      feedback.textContent = form.dataset.loading;
      open();
      timer = setTimeout(load, 180);
    });
    input.addEventListener('keydown', event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); return; }
      if (event.key === 'Enter' && active >= 0 && !panel.hidden) {
        const option = options()[active];
        if (option) { event.preventDefault(); option.click(); }
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      if (panel.hidden) { load(); return; }
      const list = options();
      if (!list.length) return;
      active = (active + (event.key === 'ArrowDown' ? 1 : -1) + list.length) % list.length;
      list.forEach((option, i) => option.setAttribute('aria-selected', String(i === active)));
      input.setAttribute('aria-activedescendant', list[active].id);
      const selected = list[active];
      if (selected.offsetTop < results.scrollTop) results.scrollTop = selected.offsetTop;
      else if (selected.offsetTop + selected.offsetHeight > results.scrollTop + results.clientHeight) results.scrollTop = selected.offsetTop + selected.offsetHeight - results.clientHeight;
    });
    form.querySelector('.ar-search-close').addEventListener('click', () => { input.focus(); close(); });
    form.addEventListener('focusout', event => { if (!form.contains(event.relatedTarget)) close(); });
    const outside = event => { if (!form.contains(event.target)) close(); };
    document.addEventListener('pointerdown', outside);
    controllers.set(form, () => { abort(); document.removeEventListener('pointerdown', outside); });
  }

  function initRotation(root) {
    if (root.dataset.arRotation) return;
    root.dataset.arRotation = 'manual';
    root.addEventListener('click', event => {
      if (event.target.closest('[role="tab"]')) alignCards();
    });
  }

  function footerDisclosures() {
    document.querySelectorAll('.ar-footer-disclosure').forEach(details => {
      details.open = !mobile.matches;
      details.querySelector('summary').tabIndex = mobile.matches ? 0 : -1;
    });
  }
  function init() {
    controllers.forEach((dispose, element) => { if (!element.isConnected) { dispose(); controllers.delete(element); } });
    document.querySelectorAll('[data-ar-search]').forEach(initSearch);
    document.querySelectorAll('[data-rf-tabs],[data-az-tabs]').forEach(initRotation);
    footerDisclosures();
    alignCards();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((dispose, element) => { if (event.target.contains(element)) { dispose(); controllers.delete(element); } });
  });
  window.addEventListener('resize', alignCards, {passive: true});
  window.addEventListener('load', alignCards, {once: true});
  document.fonts?.ready.then(alignCards);
  mobile.addEventListener('change', footerDisclosures);
})();
